import React, { useEffect, useState, useCallback, useLayoutEffect } from 'react'
import { useRouter } from 'next/router'
import Image from 'next/image'
import { firebaseClient } from 'lib/firebaseClient'
import { useUser } from 'lib/hooks'
import SEO from 'components/Seo'
import GlobalLoading from 'components/GlobalLoading'
import { generateFantasyName } from 'lib/utils'
import { FLEX_BOX_GAME } from 'games/flexbox/game-config'
import { GRID_ATTACK_GAME } from 'games/gridattack/game-config'
import Layout from 'components/Layout'

export default function EnterMe() {
  const router = useRouter()

  const authMode: 'signup' | 'signin' | undefined = router?.query?.authMode
  const redirectAfterTo: string | undefined = router?.query?.redirectAfterTo

  const [errorMsg, setErrorMsg] = useState('')
  const [loading, setLoading] = useState(true)
  const [isEnterEmptyEmailBlockShown, setEnterEmptyEmailBlockShown] = useState(false)

  const signInWithEmail = useCallback(
    async (email: string) => {
      try {
        const result = await firebaseClient.auth().signInWithEmailLink(email, window.location.href)

        if (result?.additionalUserInfo?.isNewUser) {
          const profile = {
            uid: result.user?.uid,
            games: [],
            userName: generateFantasyName(),
          }

          await firebaseClient.firestore().collection('users').doc(result.user?.uid).set(profile)

          const user = result.user
          user.profile = profile

          try {
            await FLEX_BOX_GAME.transferUnauthUserDataToAuthUser(user)
            await GRID_ATTACK_GAME.transferUnauthUserDataToAuthUser(user)
          } catch (error) {
            console.error(error)
          }

          window.localStorage.removeItem('emailForSignIn')

          if (redirectAfterTo) {
            router.push(redirectAfterTo)
          } else {
            router.push('/games')
          }
        } else {
          router.push('/games')
        }
      } catch (error) {
        console.error(error)
        setErrorMsg(error.message)
      } finally {
        setLoading(false)
      }
    },
    [redirectAfterTo, router]
  )

  useLayoutEffect(() => {
    if (firebaseClient.auth().isSignInWithEmailLink(window.location.href)) {
      const email = window.localStorage.getItem('emailForSignIn')

      if (!email) {
        setEnterEmptyEmailBlockShown(true)
        setLoading(false)
      } else {
        signInWithEmail(email)
      }
    } else {
      setLoading(false)
      router.push('/games')
    }
  }, [])

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault()

      const email = e.currentTarget.email.value

      signInWithEmail(email)
    },
    [signInWithEmail]
  )

  return (
    <>
      <GlobalLoading loading={loading} />
      <SEO noindex />
      <Layout onlyLogo withoutFooter>
        <>
          {loading && (
            <div className="flex justify-center items-center min-h-screen h-full bg-black w-screen px-4">
              <p className="text-white lg:text-2xl text-xl">
                {authMode === 'signup' ? 'Creating your profile...' : 'Loading...'}
              </p>
            </div>
          )}
          <div className="flex items-center flex-col min-h-screen h-full bg-black w-screen px-4">
            {isEnterEmptyEmailBlockShown && (
              <>
                <div className="w-full max-w-xl flex flex-col justify-center items-center">
                  <h2 className="mt-6 mb-6 text-center lg:text-3xl text-xl font-medium text-white">
                    Please enter your email address that you used to register for confirmation
                  </h2>
                </div>
                <form
                  className="w-full max-w-xs flex flex-col justify-center items-center mx-auto"
                  onSubmit={onSubmit}
                >
                  <label htmlFor="email" className="sr-only">
                    Email address
                  </label>
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="relative mb-4 block w-full px-4 py-3 border rounded-md border-gray-100 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-yellow-500 focus:z-10"
                    placeholder="Your email"
                  />
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700"
                  >
                    Confirm Email
                  </button>
                </form>
              </>
            )}
            {errorMsg && <p className="text-red-400 mt-4">{errorMsg}</p>}
          </div>
        </>
      </Layout>
    </>
  )
}
